import { LoadingOutlined } from '@ant-design/icons'
import { Spin, notification } from 'antd'
import { RcFile } from 'antd/es/upload'
import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import RichTextField from 'src/app/shared/components/common/rich-text-field'
import { USER_ROLE } from 'src/app/shared/constants/app.constants'
import { STATUS_CODES } from 'src/app/shared/constants/common.constants'
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import { useClient } from 'src/app/shared/hooks/useClient'
import { useRolePermission } from 'src/app/shared/hooks/useRolePermission'
import { ISubmitIssuedCheckFile } from 'src/app/shared/models/issuedCheck.model'
import { EPermissions, ESubModules } from 'src/app/shared/models/roleAndPermission.model'
import { getIssuedCheckFile, submitIssuedCheckFile } from 'src/app/shared/services/IssuedCheckService'
import { getUserProfile } from 'src/app/store/auth'
import { NotiDangerIcon } from 'src/assets/icons'
import { FILE_ACCEPT, ISSUED_CHECK_STATUS, LIMITED_FILE_SIZE, defaultValues } from './index.constant'
import { FormValues, SubmitIssuedCheckFileContext, SubmitIssuedCheckProps } from './index.context'

const MAXIMUM_PROCESSED_TIME = 10 // unit: second

const SubmitIssuedCheckProvider: React.FC<PropsWithChildren> = (props) => {
  const { children } = props
  const { role, userId } = useSelector(getUserProfile)
  const { t } = useTranslation()
  const {
    accounts,
    clients,
    loadingClients,
    fileMappings,
    handleGetAccounts,
    handleGetClients,
    handleGetClientInfoByUser,
    setAccounts,
    setFileMappings,
  } = useClient()
  const { userPermissions } = useRolePermission()

  const [checkList, setCheckList] = useState<SubmitIssuedCheckProps['checkList']>(null)

  const [file, setFile] = useState<RcFile | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [listLoading, setListLoading] = useState<boolean>(false)
  const form = useForm<FormValues>({
    defaultValues,
    reValidateMode: 'onChange',
  })
  const [api, contextHolder] = notification.useNotification()
  const [validatedFromBE, setValidatedFromBE] = useState<boolean>(false) // this state is used for distinguishing between validation from FE and error coming from BE
  const [startChecking, setStartChecking] = useState<boolean>(true)
  const [startCounting, setStartCounting] = useState<boolean>(false)

  const clientId =
    useWatch({
      control: form.control,
      name: 'clientId',
    }) ?? ''

  const fileMappingId = useWatch({
    control: form.control,
    name: 'mappingFormat',
  })

  const handleStatusChange = useCallback(
    (status: string | number) => {
      // hide previous noti
      api.destroy()
      // use setTimout to prevent flashes when noti displays
      setTimeout(() => {
        // clear checking interval
        setStartChecking(false)
        // end counting 10s
        if (status !== ISSUED_CHECK_STATUS.PROCESSING) {
          setStartCounting(false)
          setStartChecking(false)
        }
        // handle notificaiton
        switch (status as any) {
          case ISSUED_CHECK_STATUS.PROCESSING:
            api.info({
              message: t(`SystemMessages:${SYSTEM_MESSAGE_IDS.CHECK_FILE_PROCESSING}`),
              closeIcon: null,
              duration: 1000,
              icon: (
                <Spin
                  indicator={<LoadingOutlined style={{ fontSize: 18 }} spin />}
                  size='small'
                  style={{
                    marginBottom: 4,
                  }}
                />
              ),
            })
            break
          case ISSUED_CHECK_STATUS.ISSUED:
          case ISSUED_CHECK_STATUS.PROCESSED:
            api.success({
              message: t(`SystemMessages:${SYSTEM_MESSAGE_IDS.CHECK_SUBMITTED_SUCCESSFULLY}`),
              closeIcon: null,
            })
            break
          case ISSUED_CHECK_STATUS.PROCESSED_WITH_EXCEPTION:
            api.warning({
              message: t(`SystemMessages:${SYSTEM_MESSAGE_IDS.CHECK_SUBMITTED_SUCCESSFULLY_BUT_SOME_DUPLICATED}`),
              closeIcon: null,
              icon: <NotiDangerIcon />,
            })
            break
          case ISSUED_CHECK_STATUS.REJECTED:
            api.warning({
              message: t(`SystemMessages:${SYSTEM_MESSAGE_IDS.CHECK_SUBMITTED_SUCCESSFULLY_BUT_ALL_DUPLICATED}`),
              closeIcon: null,
              icon: <NotiDangerIcon />,
            })
            break
          case ISSUED_CHECK_STATUS.FAILED:
            api.error({
              message: t(`SystemMessages:${SYSTEM_MESSAGE_IDS.ISSUED_CHECK_FILE_SUCCESSFULLY_BUT_CHECKS_INCORRECT}`),
              closeIcon: null,
            })
            break

          default:
            break
        }
      }, 500)
      setLoading(false)
    },
    [api, t],
  )

  const handleGetCheckFile = useCallback(async () => {
    try {
      setListLoading(true)
      const res = await getIssuedCheckFile({
        userId,
      })
      if (res.statusCode === STATUS_CODES.SUCCESS) {
        setCheckList(res.data)
        if (checkList && res.data.status !== checkList.status) {
          handleStatusChange(res.data.status)
        } else if (res.data.status !== ISSUED_CHECK_STATUS.PROCESSING) {
          setStartCounting(false)
          setStartChecking(false)
        }
      }
    } catch (error) {
      console.log('ERROR 123: ', error)
      setStartChecking(false)
    } finally {
      setListLoading(false)
    }
  }, [checkList, handleStatusChange, userId])

  const resetFormAfterSubmit = useCallback(() => {
    if (role === USER_ROLE.BANK) {
      setAccounts([])
      setFileMappings([])
      form.clearErrors('accountId')
      form.clearErrors('mappingFormat')
    }
    if (role === USER_ROLE.CLIENT) {
      form.resetField('accountId')
      form.resetField('file')
      form.resetField('mappingFormat')
      return
    }
    form.reset()
  }, [form, role, setAccounts, setFileMappings])

  const onSubmit = useCallback(
    async (values: FormValues) => {
      try {
        setLoading(true)
        const data: ISubmitIssuedCheckFile.Data = {
          AccountId: values.accountId as any,
          ClientId: values.clientId as any,
          IssuedCheckFileFormatId: values.mappingFormat as any,
          UserId: userId,
          IssuedCheckFile: file as any,
          IsAllAcount: values.accountId === null,
        }

        const formData = new FormData()
        Object.entries(data).forEach((item) => {
          formData.append(item[0], item[1])
        })
        // counting 10 sec after calling api
        setStartCounting(true)
        api.info({
          message: t(`SystemMessages:${SYSTEM_MESSAGE_IDS.CHECK_FILE_PROCESSING}`),
          closeIcon: null,
          duration: 1000,
          icon: (
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 18 }} spin />}
              size='small'
              style={{
                marginBottom: 4,
              }}
            />
          ),
        })
        const res = await submitIssuedCheckFile(formData)
        if (res.statusCode === STATUS_CODES.SUCCESS) {
          setCheckList(res.data)
          setStartChecking(true)
          resetFormAfterSubmit()
        }
      } catch (error: any) {
        api.destroy()
        setStartCounting(false)
        const errorStatusCode = error?.statusCode
        if (
          [
            SYSTEM_MESSAGE_IDS.CHECK_SUBMITTED_SUCCESSFULLY_BUT_ALL_DUPLICATED,
            SYSTEM_MESSAGE_IDS.ISSUED_CHECK_FILE_SUCCESSFULLY_BUT_CHECKS_INCORRECT,
          ].includes(errorStatusCode)
        ) {
          api.error({
            message: t(`SystemMessages:${errorStatusCode}`),
            closeIcon: null,
          })
          return
        }
        if (errorStatusCode === SYSTEM_MESSAGE_IDS.INVALID_FILE_MAPPING_SETUP) {
          setValidatedFromBE(true)
          form.setError('file', {
            message: error?.message,
          })
          return
        }
        if (errorStatusCode && Object.values(SYSTEM_MESSAGE_IDS).includes(errorStatusCode)) {
          setValidatedFromBE(true)
          form.setError('file', {
            message: t(`SystemMessages:${errorStatusCode}`),
          })
          return
        } else {
          setValidatedFromBE(true)
          form.setError('file', {
            message: t(`SystemMessages:${SYSTEM_MESSAGE_IDS.FAILED_TO_SUBMIT_CHECK_FILE}`),
          })
          return
        }
      } finally {
        setLoading(false)
      }
    },
    [api, file, form, resetFormAfterSubmit, t, userId],
  )

  const beforeUpload = useCallback(
    (file: RcFile, FileList: RcFile[], fromDrag?: boolean) => {
      form.setError('file', {
        message: '',
        type: 'pattern',
      })
      form.setValue('file', file.name)
      if (file.size > LIMITED_FILE_SIZE) {
        form.setError('file', {
          message: t(`SystemMessages:${SYSTEM_MESSAGE_IDS.INVALID_SUBMIT_CHECK_FILE_SIZE}`),
          type: 'pattern',
        })
        return false
      }

      if (!Object.values(FILE_ACCEPT).includes(file.type)) {
        form.setError('file', {
          message: t(`SystemMessages:${SYSTEM_MESSAGE_IDS.INVALID_CHECK_FILE_FORMAT}`),
          type: 'pattern',
        })
        return false
      }
      form.clearErrors('file')
      setFile(file)
      form.trigger('file')
      return false
    },
    [form, t],
  )

  // clear error + value when file mapping id changes
  useEffect(() => {
    form.setValue('accountId', undefined)
    form.clearErrors('accountId')
  }, [fileMappingId, form])

  // get account list by client ID
  useEffect(() => {
    if (clientId && role !== USER_ROLE.CLIENT) {
      form.setValue('accountId', undefined)
      form.setValue('mappingFormat', null)
      form.clearErrors('accountId')
      form.clearErrors('mappingFormat')
      handleGetAccounts(clientId)
    }
  }, [clientId, form, handleGetAccounts, role])

  useEffect(() => {
    if (role !== USER_ROLE.CLIENT) {
      handleGetClients()
    } else {
      handleGetClientInfoByUser()
    }
  }, [role])

  useEffect(() => {
    if (clients.length > 0 && clients[0]?.id && role === USER_ROLE.CLIENT) {
      form.setValue('clientId', clients[0].id)
    }
  }, [clients, form, role])

  // polling to check file status
  useEffect(() => {
    const interval = setInterval(async () => {
      if (startChecking) {
        setStartChecking(true)
        await handleGetCheckFile()
      }
    }, 2000)

    if (!startChecking) {
      clearInterval(interval)
    }

    return () => {
      clearInterval(interval)
    }
  }, [handleGetCheckFile, startChecking])

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (startCounting) {
        // hide previous noti
        api.destroy()
        // use setTimout to prevent flashes when noti displays
        setTimeout(() => {
          api.warning({
            message: <RichTextField richText={t(`SystemMessages:${SYSTEM_MESSAGE_IDS.CHECK_FILE_SUBMIT_TIMEOUT}`)} />,
            closeIcon: null,
            icon: <NotiDangerIcon />,
          })
        }, 500)
      } else {
        clearTimeout(timeout)
      }
    }, MAXIMUM_PROCESSED_TIME * 1000)

    return () => {
      clearTimeout(timeout)
    }
  }, [api, startCounting, t])

  return (
    <SubmitIssuedCheckFileContext.Provider
      value={{
        form,
        clients,
        accounts,
        fileMappings,
        loading,
        loadingClients,
        listLoading,
        checkList,
        disabled:
          role === USER_ROLE.CLIENT ||
          !userPermissions[ESubModules.SUBMIT_ISSUE_CHEK_FIL]?.includes(EPermissions.UPLOAD),
        validatedFromBE,
        onSubmit,
        beforeUpload,
        setValidatedFromBE,
      }}
    >
      {contextHolder}
      {children}
    </SubmitIssuedCheckFileContext.Provider>
  )
}

export default SubmitIssuedCheckProvider
